/**
 * Easter tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterTattoosPack: ToilePack = {
    slug: 'easter-tattoos',
    category: 'season',
    name: 'Easter tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'bunny', label: 'Bunny', subject: 'A small sitting bunny with a fluffy tail and long upright ears'},
        {key: 'blossom', label: 'Blossom sprig', subject: 'A sprig of cherry blossom with three open flowers and a leaf'},
        {key: 'egg', label: 'Tiny egg', subject: 'A tiny patterned egg with a spiral and dot motif'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a lamb instead', 'as a matching pair', 'smaller and minimal'],
    directionHint: 'try: a lamb instead',
    seo: {
        title: 'Easter tattoos: bunny, blossom, tiny egg',
        metaDescription: 'Nine spring tattoo designs — a sitting bunny, a blossom sprig, a tiny egg — in simple, detailed or doodle line weight, no skin.',
        h1: 'Easter tattoo designs: bunny, blossom sprig and tiny egg',
        tagline: 'Nine designs, three spring motifs, three line weights each — drawn as a tattoo, not a picture.',
        intro: 'A spring tattoo drawn as a tattoo, not a picture: a small sitting bunny, a sprig of cherry blossom, a tiny patterned egg, each in bold simple line, intricate detailed line, or a looser hand-drawn doodle weight. Isolated on white with no skin or arm, so it reads as a reference. Ask for a different spring motif, like a lamb, and it redraws in the same three line weights.',
        useCases: [
            'a stencil reference for a spring design',
            'a print for comparing line weights',
            'a reference sheet for a tattoo consultation',
            'a starting point for a matching-pair design',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each design is centered on a square page with a wide white margin, small tattoo-flash scale.'},
            {q: 'Are these ready to tattoo?', a: 'They are designs, not stencils — an artist redraws the outline at placement size before transferring it.'},
            {q: 'Can I get a different spring motif?', a: "Yes — that's what the direction field is for. Type a different motif, like a lamb, and it redraws in the same three line weights."},
            {q: "What's the difference between the three line weights?", a: 'Simple is a bold open outline, detailed adds fine decorative line work, and doodle is a looser hand-drawn stroke — three ways the same motif reads as a tattoo.'},
        ],
    },
}
